/**
 * Mock data for the shapes workshop page.
 * Extracted from shapes-page.tsx to keep that file within the 600-line limit.
 */

export const MOCK_TEXT =
  "The deployment pipeline completed successfully. All 47 test suites passed with zero failures. The build artifact was uploaded to the staging environment and is ready for QA review.\n\nKey changes in this release:\n- Improved WebSocket reconnection logic\n- Fixed memory leak in canvas renderer\n- Added batch processing for frame updates";

export const MOCK_CODE = `import { useState, useCallback } from "react";

interface UseDebounceOptions {
  delay: number;
  leading?: boolean;
}

export function useDebounce<T extends (...args: unknown[]) => void>(
  fn: T,
  { delay, leading = false }: UseDebounceOptions
): T {
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>();

  return useCallback((...args: Parameters<T>) => {
    if (timer) clearTimeout(timer);
    if (leading && !timer) fn(...args);
    setTimer(setTimeout(() => {
      if (!leading) fn(...args);
      setTimer(undefined);
    }, delay));
  }, [fn, delay, leading, timer]) as T;
}`;

export const MOCK_SEARCH_RESULTS = [
  {
    title: "React Flow Documentation - Custom Nodes",
    snippet:
      "Learn how to create custom nodes in React Flow. Custom nodes allow you to render any React component inside a node.",
    url: "https://reactflow.dev/docs/guides/custom-nodes/",
    image: "https://picsum.photos/seed/reactflow/40/40",
    isTopPick: true,
  },
  {
    title: "Building Real-time Collaborative Canvas Applications",
    snippet:
      "A comprehensive guide to building multiplayer canvas experiences using WebSockets and CRDTs for conflict-free state management.",
    url: "https://blog.example.com/collaborative-canvas",
    image: "https://picsum.photos/seed/canvas/40/40",
  },
  {
    title: "Zustand State Management Patterns",
    snippet:
      "Advanced patterns for Zustand including middleware, persistence, and computed selectors for complex application state.",
    url: "https://docs.pmnd.rs/zustand/recipes/recipes",
    image: "https://picsum.photos/seed/zustand/40/40",
  },
];

export const MOCK_TOOL_INPUT = JSON.stringify(
  {
    query: "SELECT u.name, COUNT(p.id) as project_count FROM users u LEFT JOIN projects p ON p.owner_id = u.id GROUP BY u.id ORDER BY project_count DESC LIMIT 10",
    database: "production",
  },
  null,
  2,
);

export const MOCK_TOOL_OUTPUT = JSON.stringify(
  {
    rows: [
      { name: "Alice Chen", project_count: 12 },
      { name: "Bob Martinez", project_count: 8 },
      { name: "Carol Williams", project_count: 5 },
    ],
    total: 3,
    execution_time_ms: 42,
  },
  null,
  2,
);

export const MOCK_IMAGES = [
  {
    url: "https://picsum.photos/seed/denker1/400/300",
    alt: "Architecture diagram",
    caption: "System architecture overview",
  },
  {
    url: "https://picsum.photos/seed/denker2/400/300",
    alt: "Dashboard mockup",
    caption: "Main dashboard layout",
  },
];

export const MOCK_SINGLE_IMAGE = [
  {
    url: "https://picsum.photos/seed/denker3/500/300",
    alt: "Canvas screenshot",
    caption: "Canvas workspace with multiple frames",
  },
];

export const MOCK_HTML_CHART = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, sans-serif; padding: 12px; font-size: 11px; color: #1D1D1F; background: white; }
  .label { color: #86868B; font-size: 10px; font-weight: 600; margin-bottom: 6px; }
  .bars { display: flex; align-items: flex-end; gap: 4px; height: 60px; margin-bottom: 4px; }
  .bar { flex: 1; border-radius: 3px 3px 0 0; background: #007AFF; opacity: 0.65; }
  .bar.hi { opacity: 1; background: #34C759; }
  .bar-labels { display: flex; gap: 4px; margin-bottom: 10px; }
  .bl { flex: 1; font-size: 8px; color: #AEAEB2; text-align: center; }
  .bl.hi { color: #34C759; font-weight: 600; }
  .kpis { display: flex; gap: 5px; }
  .kpi { flex: 1; background: rgba(0,0,0,0.04); border-radius: 6px; padding: 5px 7px; }
  .kl { font-size: 8px; color: #AEAEB2; margin-bottom: 1px; }
  .kv { font-size: 13px; font-weight: 700; }
</style>
</head>
<body>
  <div class="label">Monthly Revenue</div>
  <div class="bars">
    <div class="bar" style="height:40%"></div>
    <div class="bar" style="height:55%"></div>
    <div class="bar" style="height:48%"></div>
    <div class="bar" style="height:72%"></div>
    <div class="bar hi" style="height:100%"></div>
    <div class="bar" style="height:60%;opacity:0.2;background:#AEAEB2"></div>
  </div>
  <div class="bar-labels">
    <div class="bl">Jan</div><div class="bl">Feb</div><div class="bl">Mar</div>
    <div class="bl">Apr</div><div class="bl hi">May</div><div class="bl">Jun</div>
  </div>
  <div class="kpis">
    <div class="kpi"><div class="kl">Total</div><div class="kv" style="color:#34C759">$48.2k</div></div>
    <div class="kpi"><div class="kl">Growth</div><div class="kv">+12%</div></div>
    <div class="kpi"><div class="kl">MoM</div><div class="kv">+3.4%</div></div>
  </div>
</body>
</html>`;
